/**
 * Auth Store - État global d'authentification
 *
 * Store SolidJS pour gérer l'état user de manière réactive
 * Similaire à Pinia dans Vue, mais avec createStore de SolidJS
 *
 * Note: SuperTokens SDK gère automatiquement le refresh des tokens.
 * Ce store gère uniquement l'état user pour l'UI.
 */

import { createStore } from 'solid-js/store';
import type { ProfileDto } from '~/api/model/profileDto';
import type { UserDto } from '~/api/model/userDto';
import { NINA_API_URL } from '~/config/api.config';

// The auth API returns UserDto but with a profile enriched with avatarUrl/avatarThumbnailUrl (ProfileDto fields)
export type AuthUser = Omit<UserDto, 'profile'> & { profile: ProfileDto };
type User = AuthUser;

interface AuthState {
  user: User | null;
  isLoading: boolean;
}

// Store réactif global (singleton)
const [authState, setAuthState] = createStore<AuthState>({
  user: null,
  isLoading: true,
});

// Cache pour éviter les appels multiples simultanés
let fetchPromise: Promise<User | null> | null = null;

/**
 * Fetch user profile depuis l'API
 * Avec déduplication automatique des appels simultanés
 * SuperTokens intercepte automatiquement et gère le refresh si nécessaire
 */
export async function fetchUser(): Promise<User | null> {
  // Si déjà en cours de fetch, retourner la même promesse
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      // SuperTokens SDK intercepte ce fetch et gère automatiquement :
      // - Ajout de credentials
      // - Refresh du token si expiré
      // - Retry après refresh
      const response = await fetch(`${NINA_API_URL}/auth/profile`, {
        credentials: 'include',
      });

      if (!response.ok) {
        setAuthState({ user: null, isLoading: false });
        return null;
      }

      const data = (await response.json()) as { user: User };
      setAuthState({ user: data.user, isLoading: false });
      return data.user;
    } catch {
      setAuthState({ user: null, isLoading: false });
      return null;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * Mettre à jour le user (ex: après modification profile)
 */
export function setUser(user: User | null) {
  setAuthState('user', user);
}

/**
 * Rafraîchir le user depuis l'API (force reload)
 * Utile après modification du profile
 */
export async function refreshUser(): Promise<User | null> {
  setAuthState('isLoading', true);
  return fetchUser();
}

/**
 * Clear user (logout)
 */
export function clearUser() {
  setAuthState({ user: null, isLoading: false });
}

/**
 * Export du store en lecture seule
 */
export const authStore = authState;
